import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Alert,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardActionArea,
    CardContent,
    Chip
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Info as InfoIcon,
    Folder as FolderIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { devLog } from '../../../utils/devLog';

interface ScormContent {
    packageUrl?: string;
    packageFileName?: string;
    packageSize?: number;
    scormVersion?: string;
    manifestData?: any;
    launchUrl?: string;
    completionThreshold?: number;
    masteryScore?: number;
}

interface ScormEditorProps {
    content: ScormContent;
    onChange: (content: ScormContent) => void;
}

const fetchScormVersion = async (uuid: string): Promise<string | undefined> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/scorm/extract/${uuid}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        if (!response.ok) {
            return undefined;
        }
        const data = await response.json();
        return typeof data.scormVersion === 'string' && data.scormVersion !== 'unknown'
            ? data.scormVersion
            : undefined;
    } catch {
        return undefined;
    }
};

const ScormEditor: React.FC<ScormEditorProps> = ({ content, onChange }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [scormDialogOpen, setScormDialogOpen] = useState(false);
    const [scormFiles, setScormFiles] = useState<any[]>([]);
    const [loadingFiles, setLoadingFiles] = useState(false);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.zip')) {
            notify('Bitte laden Sie eine .zip Datei hoch', { type: 'warning' });
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('fileType', 'scorm');

            // Simulate upload progress (in real implementation, use XMLHttpRequest for progress tracking)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 200);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            clearInterval(progressInterval);
            setUploadProgress(100);

            if (!response.ok) {
                throw new Error('Upload fehlgeschlagen');
            }

            const data = await response.json();
            devLog('Upload response:', data);

            // The API returns the media file object nested under 'file' key
            const uuid = data.file?.uuid || data.file?.id || data.uuid || data.id;

            if (!uuid) {
                console.error('No UUID in response:', data);
                throw new Error('Keine UUID in der Upload-Response gefunden');
            }

            const scormVersion = await fetchScormVersion(uuid);
            onChange({
                ...content,
                packageUrl: `/api/media/${uuid}`,
                packageFileName: file.name,
                packageSize: file.size,
                scormVersion,
                launchUrl: `/api/media/${uuid}/index.html`
            });

            notify('SCORM-Paket erfolgreich hochgeladen', { type: 'success' });
        } catch (error) {
            console.error('Upload error:', error);
            notify('Fehler beim Hochladen des SCORM-Pakets', { type: 'error' });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleRemovePackage = () => {
        if (window.confirm('Möchten Sie das SCORM-Paket wirklich entfernen?')) {
            onChange({
                completionThreshold: content.completionThreshold,
                masteryScore: content.masteryScore
            });
        }
    };

    const loadScormFiles = async () => {
        setLoadingFiles(true);
        setScormDialogOpen(true);
        try {
            const response = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: {}
            });

            const scorms = response.data.filter((file: any) => {
                return file.mimeType === 'application/zip' ||
                       file.originalFilename?.toLowerCase().endsWith('.zip');
            });

            setScormFiles(scorms);
        } catch (err) {
            console.error('Error loading SCORM files:', err);
        } finally {
            setLoadingFiles(false);
        }
    };

    const handleSelectScormFile = async (file: any) => {
        const scormVersion = await fetchScormVersion(file.uuid);
        onChange({
            ...content,
            packageUrl: `/api/media/${file.uuid}`,
            packageFileName: file.originalFilename,
            packageSize: file.size,
            scormVersion,
            launchUrl: `/api/media/${file.uuid}/index.html`
        });
        setScormDialogOpen(false);
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    return (
        <Box>
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    SCORM (Sharable Content Object Reference Model)
                </Typography>
                <Typography variant="body2">
                    Laden Sie ein SCORM-Paket (ZIP-Datei) hoch. Das Paket muss eine gültige imsmanifest.xml Datei enthalten.
                    Unterstützte Versionen: SCORM 1.2, SCORM 2004.
                </Typography>
            </Alert>

            {!content.packageUrl ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box
                        component="label"
                        sx={{
                            display: 'block',
                            cursor: 'pointer',
                            width: '100%'
                        }}
                    >
                        <input
                            type="file"
                            hidden
                            accept=".zip"
                            onChange={handleFileUpload}
                            disabled={uploading}
                        />
                        <Paper
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                border: '2px dashed',
                                borderColor: 'divider',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'action.hover'
                                }
                            }}
                        >
                            <UploadIcon sx={{ fontSize: 64, color: 'action.active', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                SCORM-Paket hochladen
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Klicken Sie hier oder ziehen Sie eine ZIP-Datei hierher
                            </Typography>
                            {uploading && (
                                <Box sx={{ mt: 2 }}>
                                    <LinearProgress variant="determinate" value={uploadProgress} />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                        {uploadProgress}% hochgeladen
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            oder
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<FolderIcon />}
                            onClick={loadScormFiles}
                            disabled={loadingFiles}
                        >
                            {loadingFiles ? 'Lade...' : 'Aus Mediathek wählen'}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box>
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Hochgeladenes SCORM-Paket
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary={content.packageFileName}
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" component="span">
                                                Größe: {formatFileSize(content.packageSize)}
                                            </Typography>
                                            {content.scormVersion && (
                                                <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                                                    Version: SCORM {content.scormVersion}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" onClick={handleRemovePackage} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </List>
                    </Paper>

                    <Typography variant="h6" gutterBottom>
                        SCORM-Einstellungen
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Launch URL"
                            fullWidth
                            value={content.launchUrl || ''}
                            onChange={(e) => onChange({ ...content, launchUrl: e.target.value })}
                            helperText="Relative URL zur Startdatei im SCORM-Paket (z.B. index.html, story.html)"
                        />

                        <TextField
                            label="Completion Threshold (%)"
                            type="number"
                            fullWidth
                            value={content.completionThreshold || 100}
                            onChange={(e) => onChange({ ...content, completionThreshold: parseInt(e.target.value) || 100 })}
                            helperText="Prozentsatz des Kurses, der für die Vervollständigung erreicht werden muss"
                            inputProps={{ min: 0, max: 100 }}
                        />

                        <TextField
                            label="Mastery Score (%)"
                            type="number"
                            fullWidth
                            value={content.masteryScore || 80}
                            onChange={(e) => onChange({ ...content, masteryScore: parseInt(e.target.value) || 80 })}
                            helperText="Mindestpunktzahl, die zum Bestehen erreicht werden muss"
                            inputProps={{ min: 0, max: 100 }}
                        />
                    </Box>

                </Box>
            )}

            {/* SCORM File Selection Dialog */}
            <Dialog
                open={scormDialogOpen}
                onClose={() => setScormDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon />
                        <Typography variant="h6">SCORM-Paket aus Mediathek wählen</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loadingFiles ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography>Lade SCORM-Pakete...</Typography>
                        </Box>
                    ) : scormFiles.length === 0 ? (
                        <Alert severity="info">
                            Keine ZIP-Dateien in der Mediathek gefunden. Laden Sie zuerst ein SCORM-Paket hoch.
                        </Alert>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            {scormFiles.map(file => (
                                <Card key={file.id}>
                                    <CardActionArea onClick={() => handleSelectScormFile(file)}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {file.originalFilename}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {file.folder?.name || 'Hauptordner'} • {formatFileSize(file.size)}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={new Date(file.createdAt).toLocaleDateString('de-DE')}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScormDialogOpen(false)}>
                        Abbrechen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ScormEditor;
